import {useContext, useRef, useState} from 'react'
import {toast} from 'react-toastify'
import {SupplierBookingContainer, SupplierBookingShowMessage} from './styles';
import {approveBooking, declineBooking} from './request/booking-approvals-requests';
import Busy from '../Busy';
import {getErrorMessageForStandardResponse} from '../../util/NetworkErrorUtil';
import {transformBooking} from '../../views/supplier-bookings-approvals/SupplierBookingApprovals';
import {adjustEndDate} from '../../views/requests/booking-requests';
import moment from 'moment';
import {DateFormats} from '../constants/securspace-constants';
import {AppContext} from '../../context/app-context';
import {ObservableVariables} from '../constants/observable-constants';
import {withSnackbar} from '../hocs/withSnackbar';
import SupplierBookingApprovalsDialogWrapper from './modals/SupplierBookingApprovalsDialogWrapper';
import NoMatchingInvoicesFound from '../NoMatchingInvoicesFound';
import BookingCard from "../BookingCard";

export const headerCells = [
    { id: 'bookedDates', label: 'BOOKED DATES' },
    { id: 'brokered', label: 'Brokered' },
    { id: 'orderNumber', label: 'BOOKING NUMBER' },
    { id: 'status', label: 'STATUS' },
    { id: 'recurringCharge', label: 'MONTHLY CHARGE' },
    { id: 'overageRate', label: 'Overage Rate' },
    { id: 'locationName', label: 'LOCATION' },
    { id: 'frequencyValue', label: 'FREQUENCY' },
    { id: 'assetType', label: 'EQUIPMENT TYPE' },
    { id: 'numberOfSpaces', label: 'SPACES BOOKED' },
    { id: 'initialCharge', label: 'INITIAL CHARGE' },
    { id: 'rateValue', label: 'Rate' },
];

export const modalTypes = {adjust: 'ADJUST', decline: 'DECLINE'};

const SupplierBookingList = ({
    bookingLists,
    search,
    snackbarShowMessage,
    setBookingLists,
    hideAdjustEndDate = false
}) => {

    const [openModal, setOpenModal] = useState('');
    const [selectedBooking, setSelectedBooking] = useState(null);
    const [endDate, setEndDate] = useState('');
    const [approving, setApproving] = useState(false);
    const appContext = useContext(AppContext);
    const approvingRef = useRef(false);
    const decliningRef = useRef(false);

    function handleCloseModal() {
        setOpenModal('')
    }

    const updateBookingList = (res) => {
        const updatedBooking = res.body;
        const updateData = bookingLists.map(booking => {
            if (booking.id === updatedBooking.id) {
                return transformBooking(updatedBooking);
            } else {
                return transformBooking(booking);
            }
        });
        setBookingLists(updateData);
        Busy.set(false)
        ObservableVariables.PENDING_APPROVAL_COUNT.refreshData(appContext);
    }

    function handleApproveBooking(booking) {
        if (approvingRef.current) return;
        approvingRef.current = true;
        setApproving(true);
        Busy.set(true);

        approveBooking({bookingId: booking.id})
            .then((res) => {
                updateBookingList(res);
                handleCloseModal();
                toast.success(`Successfully approved booking ${booking.orderNumber}`);
            })
            .catch((err) => {
                Busy.set(false)
                snackbarShowMessage(getErrorMessageForStandardResponse(err), "error", 15000);
                handleCloseModal();
            })
            .finally(() => {
                approvingRef.current = false;
                setApproving(false);
            })
    }

    function handleDeclineBooking(reasonDeclined) {
        if (decliningRef.current) return;
        decliningRef.current = true;
        Busy.set(true);
        const body = {
            bookingId: selectedBooking.id,
            reasonDeclined: reasonDeclined.reason,
            reasonDeclinedNote: reasonDeclined.notes
        }
        declineBooking(body)
            .then((res) => {
                updateBookingList(res);
                toast.success(`Successfully declined booking`);
                handleCloseModal();
            })
            .catch((err) => {
                Busy.set(false)
                snackbarShowMessage(getErrorMessageForStandardResponse(err), "error", 15000);
                handleCloseModal();
            })
            .finally(() => {
                decliningRef.current = false;
            })
    }

    const handleShowDeclineModal = (booking) => {
        setSelectedBooking(booking);
        setOpenModal(modalTypes.decline)
    }

    function handleDatePicker(date) {
        setEndDate(moment(date).format(DateFormats.DAY))
    }

    function adjustBookingDates() {
        Busy.set(true);
        const data = {
            bookingId: selectedBooking.id,
            endDate
        };
        adjustEndDate(data).then((res) => {
            Busy.set(false);
            const updatedBooking = res.body;
            const updateData = bookingLists.map(booking => {
                if (booking.id === updatedBooking.id) {
                    return transformBooking(updatedBooking);
                } else {
                    return transformBooking(booking);
                }
            })
            setBookingLists(updateData);
            toast.success('Successfully adjusted dates on booking!');
            handleCloseModal();
        }).catch((error) => {
            Busy.set(false);
            toast.error(getErrorMessageForStandardResponse(error));
            handleCloseModal();
        }
        )
    }

    return (
        <SupplierBookingContainer item mt={4} container flexDirection={'column'} noBookings={!bookingLists.length}>
            {!bookingLists.length ? (
                <SupplierBookingShowMessage item>
                    <NoMatchingInvoicesFound
                        message="No matching records found."
                    />
                </SupplierBookingShowMessage>
            ) : (
                bookingLists.map((booking) => {

                    const buttons = [];
                    if (['Approved', 'Active', 'Complete'].includes(booking.status) && booking.frequency !== 'RECURRING' && !hideAdjustEndDate) {
                        buttons.push(
                            {
                                label: 'Adjust End Date',
                                onClick: (bookingData) => {
                                    setSelectedBooking(bookingData);
                                    handleDatePicker(bookingData.endDate);
                                    setOpenModal(modalTypes.adjust)
                                },
                                variant: "text",
                                styles: {
                                    color: (theme) => theme.palette.success.main,
                                    border: (theme) => `1px solid ${theme.palette.success.main}`,
                                }
                            }
                        );
                    }
                    else if (booking.status === 'Pending') {
                        buttons.push(
                            {
                                label: 'Approve',
                                onClick: handleApproveBooking,
                                variant: "text",
                                styles: {
                                    color: (theme) => theme.palette.success.main,
                                    border: (theme) => `1px solid ${theme.palette.success.main}`,
                                }
                            },
                            {
                                label: 'Decline',
                                onClick: handleShowDeclineModal,
                                variant: "text",
                                styles: {
                                    color: (theme) => theme.palette.error.main,
                                    border: (theme) => `1px solid ${theme.palette.error.main}`,
                                }
                            },
                        )
                    } else if (booking.status === 'Incomplete') {
                        buttons.push(
                            {
                                label: 'Decline',
                                onClick: handleShowDeclineModal,
                                variant: "text",
                                styles: {
                                    color: (theme) => theme.palette.error.main,
                                    border: (theme) => `1px solid ${theme.palette.error.main}`,
                                }
                            }
                        )
                    }

                    return (
                        <BookingCard
                            headCells={headerCells}
                            data={booking}
                            bookingType='supplier'
                            hasAction={false}
                            isStatusShow={false}
                            buttons={buttons}
                            key={booking.id}
                            bookingTitle={booking.customerName}
                        />
                    )
                })
            )}
            <SupplierBookingApprovalsDialogWrapper
                openModal={openModal}
                booking={selectedBooking}
                handleClose={handleCloseModal}
                onSubmitDecline={handleDeclineBooking}
                adjustBookingDates={adjustBookingDates}
                endDate={endDate}
                handleDatePicker={handleDatePicker}
            />
        </SupplierBookingContainer>
    )
}

export default withSnackbar(SupplierBookingList)
